'use client';

import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

export interface MenuItem {
  name: string;
  handle: string;
  sub_menus: MenuItem[];
  category_id?: string;
  visible?: boolean;
}

/** Drop items the admin has hidden in menu_config (visible: false), at every level. */
function onlyVisible(items: MenuItem[]): MenuItem[] {
  return items
    .filter((item) => item.visible !== false)
    .map((item) => ({ ...item, sub_menus: onlyVisible(item.sub_menus || []) }));
}

interface MenuDropdownProps {
  item: MenuItem;
  activeDropdown: string | null;
  setActiveDropdown: (handle: string | null) => void;
  isMobile: boolean;
  onMenuOpen?: () => void;
}

function MenuDropdown({ item, activeDropdown, setActiveDropdown, isMobile, onMenuOpen }: MenuDropdownProps) {
  const isOpen = activeDropdown === item.handle;

  const handleMouseEnter = () => {
    if (item.sub_menus.length > 0 && !isMobile) {
      setActiveDropdown(item.handle);
      onMenuOpen?.(); // Close search dropdown when menu opens
    }
  };

  const handleMouseLeave = () => {
    // Let the container handle closing
  };

  const handleClick = (e: React.MouseEvent) => {
    if (item.sub_menus.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen) {
        onMenuOpen?.(); // Close search dropdown when menu opens
      }
      setActiveDropdown(isOpen ? null : item.handle);
    }
  };

  if (item.sub_menus.length === 0) {
    return (
      <Link
        href={`/${item.handle}`}
        className="flex min-h-[44px] items-center whitespace-nowrap text-[14px] font-medium text-hv-ink transition-colors hover:text-hv-orange md:text-[15px]"
        prefetch={true}
      >
        {item.name}
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={handleClick}
        aria-expanded={isOpen}
        className={`relative flex min-h-[44px] items-center gap-1 whitespace-nowrap text-[14px] font-medium transition-colors md:text-[15px] ${isOpen ? 'text-hv-orange' : 'text-hv-ink hover:text-hv-orange'}`}
      >
        <span>{item.name}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        <span className={`absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-hv-orange transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0'}`} />
      </button>
    </div>
  );
}

interface CategoryMenuFixedProps {
  menuData: MenuItem[];
  onMenuOpen?: () => void;
}

export function CategoryMenuFixed({ menuData: rawMenuData, onMenuOpen }: CategoryMenuFixedProps) {
  const menuData = useMemo(() => onlyVisible(rawMenuData), [rawMenuData]);
  const [mobileTop, setMobileTop] = useState(97);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedSubMenus, setExpandedSubMenus] = useState<Record<string, boolean>>({});
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Close dropdown when navigation happens (pathname changes)
  useEffect(() => {
    setActiveDropdown(null);
    setExpandedSubMenus({});
  }, [pathname]);

  useEffect(() => {
    setIsClient(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial value
    checkMobile();

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleContainerMouseLeave = (e: React.MouseEvent) => {
    if (!isMobile) {
      // Check if we're leaving to the dropdown
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (relatedTarget &&
        relatedTarget instanceof Node &&
        dropdownRef.current &&
        dropdownRef.current.contains(relatedTarget)) {
        return; // Don't close if moving to dropdown
      }
      setActiveDropdown(null);
    }
  };

  // Close dropdown when clicking outside or on search/header elements
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      const target = event.target as Node | null;
      const targetElement = target as HTMLElement;

      // NEVER interfere with Link clicks - let navigation happen naturally
      const isLink = targetElement?.tagName === 'A' || targetElement?.closest('a');
      if (isLink) {
        return; // Don't close menu, let navigation happen
      }

      // Check if click is on search input or other header interactive elements
      const isSearchInput = targetElement?.tagName === 'INPUT' && targetElement?.getAttribute('type') === 'search';
      const isInCategoryMenu = containerRef.current?.contains(target);
      const isInDesktopDropdown = dropdownRef.current?.contains(target);
      const isInMobileDropdown = mobileDropdownRef.current?.contains(target);

      // Close if clicking outside menu/dropdown OR if clicking on search
      if (
        target &&
        ((!isInCategoryMenu && !isInDesktopDropdown && !isInMobileDropdown) || isSearchInput)
      ) {
        // Fix for ghost clicks: Don't close if clicking the specific mobile overlay
        // The overlay has its own handler which prevents event probagation and handles closing properly
        if (targetElement?.id === 'mobile-menu-overlay') return;

        setActiveDropdown(null);
      }
    };

    // Close dropdown when search input is focused
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      const isSearchInput = target?.tagName === 'INPUT' && target?.getAttribute('type') === 'search';

      if (isSearchInput) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  // Auto-expand all sub-menus on mobile when opened (Request #4)
  useEffect(() => {
    if (isMobile && activeDropdown) {
      const activeItem = menuData.find(item => item.handle === activeDropdown);
      if (activeItem && activeItem.sub_menus.length > 0) {
        const allSubMenuHandles = activeItem.sub_menus.reduce((acc, sub) => {
          acc[sub.handle] = true;
          return acc;
        }, {} as Record<string, boolean>);

        setExpandedSubMenus(prev => ({
          ...prev,
          ...allSubMenuHandles
        }));
      }
    }
  }, [activeDropdown, isMobile, menuData]);

  const activeItem = menuData.find(item => item.handle === activeDropdown);
  const showDropdown = activeDropdown && activeItem && activeItem.sub_menus.length > 0;

  // Mobile panel opens right under the header, whatever its height
  useEffect(() => {
    if (isMobile && showDropdown && containerRef.current) {
      setMobileTop(Math.round(containerRef.current.getBoundingClientRect().bottom));
    }
  }, [isMobile, showDropdown]);

  // Don't render anything if menuData is empty - prevents flash of just Hardware/Accessories
  if (menuData.length === 0) {
    return null;
  }

  // Always render the same structure - no mounted check to prevent flicker
  // Interactive functionality is enabled after mount via event handlers
  return (
    <>
      <div
        className="relative category-menu-fixed border-t border-hv-line"
        ref={containerRef}
        onMouseLeave={handleContainerMouseLeave}
      >
        <div className="mx-auto w-full max-w-[1440px] lg:px-10 xl:px-16">
          <ul className="scrollbar-hide flex items-center gap-5 overflow-x-auto px-4 lg:gap-7 lg:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
            {menuData.map((item) => (
              <li key={item.handle} className="flex-shrink-0">
                <MenuDropdown
                  item={item}
                  activeDropdown={activeDropdown}
                  setActiveDropdown={setActiveDropdown}
                  isMobile={isMobile}
                  onMenuOpen={onMenuOpen}
                />
              </li>
            ))}


          </ul>
        </div>

        {/* Desktop dropdown - positioned inside the relative container */}
        {showDropdown && !isMobile && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-[60] border-y border-hv-line bg-white font-lexend shadow-[0_24px_40px_-20px_rgba(35,34,36,0.25)]"
            onMouseEnter={() => setActiveDropdown(activeItem.handle)}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            <div className="mx-auto w-full max-w-[1440px] px-10 py-9 xl:px-16">
              <div className="mb-6 flex items-baseline justify-between border-b border-hv-line pb-4">
                <span className="text-2xl font-semibold tracking-[-0.01em] text-hv-ink">{activeItem.name}</span>
                <Link href={`/${activeItem.handle}`} className="text-sm font-semibold text-hv-orange hover:underline" prefetch={true}>Shop all {activeItem.name.toLowerCase()} →</Link>
              </div>
              <div className="flex gap-14">
                {(() => {
                  // Logic to balance columns: 3 columns maximum for clarity
                  const columns: MenuItem[][] = [[], [], []];
                  const colHeights = [0, 0, 0];

                  // Estimate height of each sub-menu (header + items)
                  const sortedSubMenus = [...activeItem.sub_menus].sort((a, b) => b.sub_menus.length - a.sub_menus.length);

                  sortedSubMenus.forEach(subItem => {
                    const height = 4 + (subItem.sub_menus.length * 1.5); // Header weight + items
                    // Find column with least height
                    const minHeightIndex = colHeights.indexOf(Math.min(...colHeights));
                    columns[minHeightIndex].push(subItem);
                    colHeights[minHeightIndex] += height;
                  });

                  return columns.map((col, idx) => col.length > 0 && (
                    <div key={idx} className="flex-1 space-y-8">
                      {col.map((subItem) => (
                        <div key={subItem.handle} className="space-y-2">
                          <h4 className="pb-1 font-hv-mono text-[11px] font-normal uppercase tracking-[0.06em] text-hv-orange">
                            {subItem.sub_menus.length > 0 ? (
                              <span
                                className="cursor-default"
                              >
                                {subItem.name}
                              </span>
                            ) : (
                              <Link
                                href={`/${subItem.handle}`}
                                className="hover:underline"
                                prefetch={true}
                              >
                                {subItem.name}
                              </Link>
                            )}
                          </h4>
                          {subItem.sub_menus.length > 0 && (
                            <ul className="space-y-0.5">
                              {subItem.sub_menus.map((subSubItem) => (
                                <li key={subSubItem.handle}>
                                  <Link
                                    href={`/${subSubItem.handle}`}
                                    className="block py-1 text-[15px] text-hv-ink transition-colors hover:text-hv-orange"
                                    prefetch={true}
                                  >
                                    {subSubItem.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile dropdown using Portal - only render after client-side hydration */}
      {isClient && showDropdown && isMobile && createPortal(
        <>
          {/* Overlay backdrop - Click to close */}
          <div
            id="mobile-menu-overlay"
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[9998] transition-opacity duration-300"
            style={{ top: `${mobileTop}px` }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setActiveDropdown(null);
            }}
          />
          {/* Menu content - Clicks here don't close menu */}
          <div
            ref={mobileDropdownRef}
            className="fixed left-0 right-0 bg-white font-lexend z-[9999] overflow-y-auto shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] animate-in slide-in-from-top-5 duration-300 border-t border-gray-100/50"
            style={{
              top: `${mobileTop}px`,
              maxHeight: `calc(100vh - ${mobileTop}px)`
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="max-w-7xl mx-auto px-6 py-6 pb-10">
              <div className="space-y-3">
                {activeItem.sub_menus.map((subItem) => {
                  const isExpanded = expandedSubMenus[subItem.handle];
                  const hasChildren = subItem.sub_menus.length > 0;

                  return (
                    <div key={subItem.handle} className="group">
                      <div
                        className={`
                          flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-200
                          ${isExpanded ? 'bg-hv-paper' : 'hover:bg-hv-paper'}
                        `}
                      >
                        {hasChildren ? (
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedSubMenus(prev => ({
                                ...prev,
                                [subItem.handle]: !prev[subItem.handle]
                              }));
                            }}
                            className="flex-1 text-[17px] font-medium text-hv-ink cursor-pointer select-none"
                          >
                            {subItem.name}
                          </div>
                        ) : (
                          <Link
                            href={`/${subItem.handle}`}
                            className="flex-1 text-[17px] font-medium text-hv-ink"
                            prefetch={true}
                          >
                            {subItem.name}
                          </Link>
                        )}
                        {hasChildren && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedSubMenus(prev => ({
                                ...prev,
                                [subItem.handle]: !prev[subItem.handle]
                              }));
                            }}
                            className={`
                              p-2 rounded-full transition-all duration-200
                              ${isExpanded ? 'bg-white text-hv-orange shadow-sm' : 'text-hv-soft'}
                            `}
                          >
                            <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </div>

                      {/* Sub-sub menus (Accordion content) */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isExpanded ? 'max-h-[500px] opacity-100 mt-2' : 'max-h-0 opacity-0'
                          }`}
                      >
                        <div className="pl-4 pr-2 space-y-1">
                          {subItem.sub_menus.map((subSubItem) => (
                            <Link
                              key={subSubItem.handle}
                              href={`/${subSubItem.handle}`}
                              className="block rounded-lg px-4 py-2.5 text-[15px] text-hv-ink transition-colors hover:bg-hv-paper hover:text-hv-orange active:text-hv-orange"
                              prefetch={true}
                            >
                              {subSubItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}